import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { caseEsito, daIso, GIORNI_BREVI, isoGiorno, meseSeDiverso, TIPI, quantiTesto, raggruppaPerCliente } from '../../utils/agenda';
import { adessoRoma, linkChiama } from '../../utils/percorso';
import Icona from '../../components/cliente/Icone';
import Tuttofare from '../../components/staff/Tuttofare';
import ListaClienti, { linkCliente } from '../../components/staff/ListaClienti';
import EsitoTelefonata from '../../components/staff/EsitoTelefonata';
import { ConsiglioRiga, Suggerito } from '../../components/staff/Consiglio';
import { EsitoVisita, Spunta, linkVoce, visitaSenzaEsito } from '../../components/staff/CardVoce';
import { Iniziale } from '../../components/staff/Persone';
import CosaVoce from '../../components/staff/CosaVoce';
import FissaVisita from '../../components/staff/FissaVisita';
import AzioniVisita from '../../components/staff/AzioniVisita';
import { visitaDaVoce } from '../../utils/visite';
import { AnnuncioBreve } from '../../components/scheda/Ricerche';

// /admin (admin) e /collaboratore: "Oggi", la bozza design/admin-oggi-v4.html. Intestazione con la frase di
// riepilogo, filtro Le mie / l'altro admin / Tutte, Tuttofare, cinque numeri, le attività di oggi con i consigli e i
// suggeriti, "Dai clienti" delle ultime 24 ore, clienti fermi, la giornata dell'altro admin, prossime scadenze,
// lista dei clienti. Dati: GET /api/oggi (backend/oggi.py).

const TAG = { visita_richiesta: 't-g', esito: 't-w', documenti: 't-b', link: 't-o', nuovo: 't-b', disponibilita: 't-v', annunci_nuovi: 't-g' };
const PICCOLO = { telefonata: 'tel', visita: 'visita', scadenza: 'scad', rogito: 'rogito', documento: 'doc', compleanno: 'auguri', nota: 'nota', altro: 'da fare' };

// "**1 telefonata** da fare" → testo con i grassetti
export const Frase = ({ testo }) => (
  <p>{String(testo || '').split('**').map((pezzo, i) => (i % 2 ? <b key={i}>{pezzo}</b> : <React.Fragment key={i}>{pezzo}</React.Fragment>))}</p>
);

const linkAzione = (e) => e.azione?.link || '#';

// L'orologio di Roma, come in agenda (pages/staff/Agenda.jsx): la giornata si rileggeva una volta sola, così a
// mezzanotte "Oggi" restava al giorno prima per sempre, con le cose del giorno finito.
const OGNI_MEZZO_MINUTO = 30000;

// La coda dell'intestazione segue l'ora, come il saluto che arriva dal server: alle 22, sotto "Buonasera · …",
// "2 cose prima di sera" parlava di una sera già arrivata. Il saluto è la fonte (è l'ora di Roma del server);
// se non si riconosce, l'orologio di Roma della pagina.
export const ORA_SERA = 18;
export const codaGiornata = (saluto, adesso = adessoRoma()) => {
  const s = String(saluto || '').trim().toLowerCase();
  if (s.startsWith('buonasera')) return 'ancora da fare';
  if (s.startsWith('buongiorno') || s.startsWith('buon pomeriggio')) return 'prima di sera';
  return adesso.getHours() >= ORA_SERA ? 'ancora da fare' : 'prima di sera';
};

// La fascia della giornata nell'ora di Roma, spaccata come la spacca il server (backend/oggi.py, `saluto`:
// mattina fino alle 12, pomeriggio fino alle 18, poi sera). Il saluto arrivava dal server una volta sola e la
// pagina si rilegge solo al cambio di giorno: lasciata aperta dalla mattina, alle 20 diceva ancora "Buongiorno" e
// "6 cose prima di sera", di una sera già arrivata. Ora la fascia la decide l'orologio di Roma della pagina (che si
// rilegge ogni mezzo minuto): saluto e coda cambiano da soli quando l'ora passa, senza aspettare una ricarica.
export const ORA_POMERIGGIO = 12;
export const FASCE = ['mattina', 'pomeriggio', 'sera'];
export const SALUTI = { mattina: 'Buongiorno', pomeriggio: 'Buon pomeriggio', sera: 'Buonasera' };
export const fasciaGiornata = (adesso = adessoRoma()) => {
  const h = adesso.getHours();
  return h < ORA_POMERIGGIO ? 'mattina' : h < ORA_SERA ? 'pomeriggio' : 'sera';
};
// "Buongiorno · giovedì 17 settembre" alle 20 diventa "Buonasera · giovedì 17 settembre": cambia il saluto, non il
// giorno (quello resta quello del server, che è l'unico a sapere di che giornata sta parlando). La giornata va
// avanti e non indietro: il saluto del server si sposta solo in avanti, così una risposta che dice già "Buonasera"
// non torna "Buongiorno" (il giorno nuovo lo porta la rilettura di mezzanotte, con il saluto rifatto dal server).
export const salutoOra = (saluto, adesso = adessoRoma()) => {
  const s = String(saluto || '').trim();
  const adessoF = fasciaGiornata(adesso);
  const pezzi = /^(buongiorno|buon pomeriggio|buonasera)(.*)$/i.exec(s);
  if (!pezzi) return s || SALUTI[adessoF];  // un saluto che non si riconosce si lascia com'è (non è nostro da riscrivere)
  const dalServer = { buongiorno: 'mattina', 'buon pomeriggio': 'pomeriggio', buonasera: 'sera' }[pezzi[1].toLowerCase()];
  const avanti = FASCE.indexOf(adessoF) > FASCE.indexOf(dalServer) ? adessoF : dalServer;
  return `${SALUTI[avanti]}${pezzi[2]}`;
};

// Di chi è la giornata: con il filtro "Tutte" il server lo dice (`tutte`) e manda un nome nullo (o "tutte"), e
// l'intestazione parla di tutti — "5 cose per tutti prima di sera" — invece di intestarsi a me. Se il nome
// arrivasse ancora, vale la scelta rimandata indietro (dati.persona).
export const perTuttiLoStaff = (nome, scelta, tutte = null) => {
  if (tutte === true) return true;
  const n = String(nome || '').trim().toLowerCase();
  return !n || n === 'tutte' || n === 'tutti' || scelta === 'tutte';
};

// "tutti" e "tutte" sono la stessa scelta per il server (backend/agenda.py, `tutti_o_tutte`)
export const chiedeTutte = (persona) => ['tutti', 'tutte'].includes(String(persona || '').trim().toLowerCase());
// Chi ha il chip "Tutte": l'admin, e chi ha qualcun altro da guardare (il responsabile con il suo gruppo, che nelle
// persone visibili si vede insieme a sé). Il collaboratore che ha solo sé stesso non ce l'ha: "tutti" per lui è la
// sua giornata e basta. Prima il chip compariva anche solo perché "tutte" era scritto nell'indirizzo, così
// "/collaboratore?persona=tutti" restava lì e l'intestazione prometteva "1 cosa per tutti" con dentro le sole sue
// cose. Ora "tutti"/"tutte" fa la stessa strada di un id che non è suo: l'indirizzo si ripulisce e si torna alla
// propria giornata.
export const puoVedereTutte = (dati = {}, isAdmin = false) => Boolean(isAdmin) || (dati.persone || []).length > 1;

// I suggeriti sono tagliati ai primi sei dal server (backend/oggi.py, `suggeriti[:6]`), ma l'intestazione e la frase
// li contano tutti: "8 mosse suggerite" con sei righe a video, e le altre due da nessuna parte. Il totale lo manda il
// server in un campo a parte (`suggeriti_totali`); se non arriva si dice che ce ne sono altre senza il numero, e solo
// quando l'elenco è pieno — sotto il taglio non ne resta nessuna. In agenda ci sono tutte (GET /api/agenda/suggeriti).
export const TAGLIO_SUGGERITI = 6;
export const altriSuggeriti = (dati = {}) => {
  const mostrati = (dati.suggeriti || []).length;
  const totale = [dati.suggeriti_totali, dati.suggeriti_totale, dati.suggeriti_quanti, dati.numeri?.suggeriti]
    .map((x) => Number(x)).find((x) => Number.isFinite(x) && x >= mostrati);
  if (totale === undefined) return mostrati >= TAGLIO_SUGGERITI ? { quanti: null } : null;
  return totale > mostrati ? { quanti: totale - mostrati } : null;
};
export const testoAltriSuggeriti = (quanti) => (quanti == null
  ? 'Ce ne sono altre in agenda'
  : quanti === 1 ? "E un'altra mossa suggerita in agenda" : `E altre ${quanti} mosse suggerite in agenda`);

// Anche "Prossime scadenze" è tagliato ai primi sei dal server (backend/oggi.py, `prossime_scadenze`, `[:6]`): la
// settima spariva in silenzio, e il riquadro sembrava quello di chi ne ha sei. Il totale lo manda il server in un
// campo a parte (`scadenze_totali`, sempre presente, anche sotto il taglio), come per i suggeriti e per la ricerca
// globale. Qui, a differenza dei suggeriti, senza il campo non si scrive niente: un elenco pieno non è per forza
// tagliato (le scadenze del prossimo mese possono essere sei esatte), e un avviso inventato sarebbe peggio del taglio.
export const altreScadenze = (dati = {}) => {
  const mostrate = (dati.scadenze || []).length;
  const totale = [dati.scadenze_totali, dati.scadenze_totale, dati.scadenze_quante]
    .map((x) => Number(x)).find((x) => Number.isFinite(x) && x >= mostrate);
  return totale > mostrate ? { quante: totale - mostrate } : null;
};
export const testoAltreScadenze = (quante) => (quante === 1 ? "E un'altra scadenza in agenda" : `E altre ${quante} scadenze in agenda`);

// La giornata dell'altro ("Karolina oggi") arriva intera dal server (`altri_oggi`): il taglio alle prime cinque è
// della pagina, ed era silenzioso — otto cose nella sua giornata, cinque a video e le altre tre da nessuna parte.
// Come "Clienti fermi" ("Tutti (N) →"), i suggeriti e le scadenze: si dice quante ne restano e si porta alla
// giornata di quella persona ("?persona=<id>", terzo giro A5), dove ci sono tutte.
export const TAGLIO_ALTRI_OGGI = 5;
export const altreVoci = (voci = []) => (voci.length > TAGLIO_ALTRI_OGGI ? voci.length - TAGLIO_ALTRI_OGGI : 0);
export const testoAltreVoci = (quante, nome) => (quante === 1
  ? `E un'altra cosa di ${nome}` : `E altre ${quante} cose di ${nome}`);

const minutiFa = (secondi) => {
  if (secondi == null) return 'mai';
  if (secondi < 60) return `${secondi} s fa`;
  if (secondi < 3600) return `${Math.round(secondi / 60)} min fa`;
  return `${Math.round(secondi / 3600)} ore fa`;
};

const RigaVoce = ({ v, onCambia, isAdmin, senzaCliente = false }) => {
  const [esito, setEsito] = useState(false);
  const telefonata = v.tipo === 'telefonata' && v.stato === 'da_fare';
  const link = linkVoce(v);
  const tel = linkChiama(v.telefono);
  // la visita già passata senza esito chiede "Com'è andata?" (la spunta "fatta" senza esito il server non la accetta)
  const daEsito = visitaSenzaEsito(v);
  const visita = v.tipo === 'visita' && v.stato === 'da_fare' && v.rif?.visita_id && !daEsito;
  const classi = [v.stato === 'fatta' && 'fatta', (visita || daEsito) && 'sotto'].filter(Boolean).join(' ');
  return (
    <div className={classi || undefined} data-testid={`oggi-${v.id}`}>
      <div className="h">{v.ora || '—'}<small>{v.stato === 'fatta' ? 'fatta' : PICCOLO[v.tipo] || v.tipo}</small></div>
      <div style={{ minWidth: 0 }}>{v.cosa ? <CosaVoce cosa={v.cosa} tipo={v.tipo} voce={v} senzaCliente={senzaCliente} /> : <b>{v.titolo}</b>}</div>
      <div className="azioni">
        {telefonata && (v.immobile_id || v.agenzia_id) && <button type="button" className="btn sm p" onClick={() => setEsito(!esito)} aria-expanded={esito}>Esito</button>}
        {telefonata && tel && !esito && <a className="btn sm" href={tel} aria-label="Chiama"><Icona nome="telefono" /></a>}
        {visita && (
          <AzioniVisita visita={visitaDaVoce(v)} cliente={{ id: v.cliente_id, nome: v.cliente_nome }} onFatto={onCambia} piccoli />
        )}
        {daEsito && <EsitoVisita voce={v} onCambia={onCambia} piccoli />}
        {!telefonata && link && <Link className="btn sm" to={link}>Apri</Link>}
        <Spunta voce={v} onCambia={onCambia} />
      </div>
      {v.consiglio && <ConsiglioRiga consiglio={v.consiglio} onFatto={onCambia} />}
      {/* telefonata per più case della stessa agenzia: l'esito si dà per tutte insieme, una riga per casa
          (docs/piu-case-agenzia.md) */}
      {esito && (
        <div style={{ gridColumn: '1/-1' }}>
          <EsitoTelefonata voce={v} isAdmin={isAdmin} case={caseEsito(v)} nomeAgenzia={v.agenzia?.etichetta}
            nomeCliente={v.cliente_breve} onFatto={() => { setEsito(false); onCambia(); }} />
        </div>
      )}
    </div>
  );
};

const Oggi = () => {
  const { userType } = useAuth();
  const navigate = useNavigate();
  const isAdmin = userType === 'admin';
  // Il filtro persona sta nell'indirizzo ("?persona=<id>"), come "?giorno=" e "?mese=" in agenda: prima era solo in
  // memoria, così la ricarica e una seconda scheda lo perdevano e "Indietro" usciva dalla pagina invece di tornare
  // alla persona di prima. "Le mie" è senza parametro (è quello che fa il server senza filtro).
  const [params, setParams] = useSearchParams();
  // la giornata di una persona è questa stessa pagina con il filtro nell'indirizzo: "/admin?persona=<id>" per
  // l'admin, "/collaboratore?persona=<id>" per il collaboratore (il percorso è quello dove si è già)
  const { pathname } = useLocation();
  const linkGiornata = (id) => `${pathname}?persona=${encodeURIComponent(id)}`;
  const persona = (params.get('persona') || '').trim() || null;
  const setPersona = useCallback((id, opzioni) => setParams((vecchi) => {
    const tutti = Object.fromEntries(vecchi);
    if (id) tutti.persona = id; else delete tutti.persona;
    return tutti;
  }, opzioni), [setParams]);
  const [dati, setDati] = useState(null);
  const [giro, setGiro] = useState(0);
  const [fissa, setFissa] = useState(null);  // {clienteId, clienteNome, immobileId} dalla card "Dai clienti"
  const ricarica = useCallback(() => setGiro((g) => g + 1), []);
  const riprova = useCallback(() => { setDati(null); setGiro((g) => g + 1); }, []);
  // «Ancora disponibile» / «Non più online» dalla card delle case da verificare: si risponde da qui, senza aprire
  // la scheda — sono case che aspettano un sì o un no, non un'istruttoria
  const rispondiVerifica = useCallback(async (casa, azione) => {
    try {
      await axios.post(`${API}/immobili/${casa.id}/${azione}`);
      toast.success(azione === 'ancora-disponibile'
        ? `${casa.titolo}: segnata come ancora disponibile`
        : `${casa.titolo}: segnata come non più online`);
      setGiro((g) => g + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Non sono riuscito a salvare. Riprovate.');
    }
  }, []);
  // L'orologio di Roma: alla mezzanotte di Roma la giornata cambia e la pagina si rilegge da sola (come l'agenda)
  const [adesso, setAdesso] = useState(adessoRoma);
  useEffect(() => {
    const t = setInterval(() => setAdesso(adessoRoma()), OGNI_MEZZO_MINUTO);
    return () => clearInterval(t);
  }, []);
  const giornata = isoGiorno(adesso);

  const personaScritta = params.get('persona');
  useEffect(() => {
    // "?persona=" vuoto: vale "Le mie", e sparisce dall'indirizzo come un id storto
    if (personaScritta !== null && !persona) { setPersona(null, { replace: true }); return; }
    axios.get(`${API}/oggi`, { params: { persona: persona || undefined } }).then((r) => {
      // "?persona=tutti" scritto a mano da chi non ha il chip "Tutte": il server risponde 200 con la sola giornata
      // di chi guarda, ma l'intestazione prometteva "1 cosa per tutti". Stessa strada di un id che non è suo:
      // l'indirizzo si ripulisce e si torna alla propria giornata
      if (chiedeTutte(persona) && !puoVedereTutte(r.data, isAdmin)) { setPersona(null, { replace: true }); return; }
      setDati(r.data);
    }).catch((e) => {
      // "?persona=" con un id che non esiste (400) o che non posso vedere (403): si torna alla mia giornata e
      // l'indirizzo si ripulisce, invece di lasciare la pagina bloccata sull'errore
      if (persona && [400, 403].includes(e?.response?.status)) { setPersona(null, { replace: true }); return; }
      setDati(false);
    });
  }, [persona, personaScritta, giro, giornata, isAdmin, setPersona]);

  // Con un errore si può riprovare senza ricaricare la pagina a mano
  if (dati === false) {
    return (
      <main className="st wrap" data-testid="oggi-errore">
        <p className="vuoto" role="alert">Non riesco a caricare la giornata. Riprovate tra poco.</p>
        <button type="button" className="btn sm p" onClick={riprova}>Riprova</button>
      </main>
    );
  }
  if (!dati) return <main className="st wrap"><p className="vuoto">Carico la giornata…</p></main>;

  const n = dati.numeri;
  // L'admin vede l'altro admin; il responsabile di gruppo i membri del suo gruppo
  const scelta = dati.persona;
  const altriSoliti = (dati.persone || []).filter((p) => p.id !== dati.io && (isAdmin ? p.ruolo === 'admin' : true));
  // La persona che si sta guardando ha sempre il suo chip, anche quando non è tra quelle dei chip: l'indirizzo può
  // portare chiunque il server lasci vedere ("/admin?persona=u-ernesto" a un admin, che ha i chip dei soli admin;
  // "?persona=tutte" a un collaboratore, che il chip "Tutte" non ce l'ha), e prima l'intestazione diceva "Ernesto,
  // 1 cosa" o "per tutti" senza che nessun chip fosse acceso: sembrava la propria giornata.
  const altrui = Boolean(scelta) && scelta !== dati.io && scelta !== 'tutte';
  const senzaChip = altrui && !altriSoliti.some((p) => p.id === scelta);
  const altri = senzaChip
    ? [...altriSoliti, (dati.persone || []).find((p) => p.id === scelta) || { id: scelta, nome: dati.nome || 'Altro' }]
    : altriSoliti;
  const nomeScelta = (dati.persone || []).find((p) => p.id === scelta)?.nome || dati.nome || 'un collega';
  const est = dati.estrattore;
  // il riquadro degli annunci che aspettano c'è solo se ce n'è almeno uno (e solo per chi vede l'estrattore)
  const nonLetti = est?.annunci_non_letti?.quanti > 0 ? est.annunci_non_letti : null;
  // case attive con un link che l'estrattore non sa leggere: non saranno lette mai, e nessuno se ne accorgeva
  const linkStorti = dati.link_non_leggibili || [];
  // case su cui l'estrattore non sa decidere: le guarda una persona, da qui, senza aprire la scheda
  const daVerificare = dati.da_verificare_a_mano || [];
  // case che il portale dà per vendute o affittate: è la telefonata da fare, e la casa da sostituire se era in
  // corsa. Al cliente non arriva nessuna notifica: lo avvisiamo noi (backend/oggi.py, `case_vendute`)
  const vendute = dati.vendute || [];
  // il backup del database: se è vecchio, qualcosa si è fermato e lo deve sapere chi può rimetterlo in piedi
  const backup = dati.backup;
  // intestazione: di chi è la giornata (o di tutti) e la coda che segue l'ora
  const tutti = perTuttiLoStaff(dati.nome, scelta, dati.tutte);
  // fra il cambio di fascia e la risposta del server (e con la risposta vecchia in mano) vince l'orologio di Roma
  const saluto = salutoOra(dati.saluto, adesso);
  const coda = codaGiornata(saluto, adesso);
  // giornata vuota: "Sandro, 0 cose prima di sera" (e sotto, due volte, "Niente in agenda per oggi.") diventa
  // "Sandro, niente da fare" — senza la coda, che con lo zero non ha più niente da dire
  const vuota = dati.cose === 0;
  const quante = <em>{quantiTesto(dati.cose, 'cosa', 'cose', tutti ? 'Niente da fare' : 'niente da fare')}</em>;
  const altri6 = altriSuggeriti(dati);
  const altreScad = altreScadenze(dati);
  return (
    <main className="st wrap" data-testid="oggi">
      <div className="head r" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '1.5rem', marginBottom: '1.2rem', flexWrap: 'wrap' }}>
        <div>
          <div className="k" data-testid="saluto">{saluto}</div>
          <h1 style={{ fontSize: 'clamp(1.9rem,3.6vw,2.6rem)', marginTop: '.3rem' }}>
            {tutti ? <>{quante} per tutti{vuota ? '' : ` ${coda}`}</> : <>{dati.nome}, {quante}{vuota ? '' : ` ${coda}`}</>}
          </h1>
          <div className="muto" style={{ marginTop: '.35rem', fontSize: '.92rem', maxWidth: '64ch' }}><Frase testo={dati.frase} /></div>
          {/* chi guarda la giornata di un altro lo legge scritto, e torna alla sua con un clic */}
          {altrui && (
            <div className="muto altrui" data-testid="giornata-altrui">
              State guardando la giornata di {nomeScelta} · <button type="button" className="link" onClick={() => setPersona(null)}>torna alle vostre</button>
            </div>
          )}
        </div>
        <div className="people" role="group" aria-label="Di chi">
          {/* "Le mie" è la giornata di chi guarda: senza parametro, come la apre il server */}
          <button type="button" className={`pp${scelta === dati.io ? ' on' : ''}`} onClick={() => setPersona(null)}>
            <Iniziale persona={(dati.persone || []).find((p) => p.id === dati.io)} taglia={28} />Le mie
          </button>
          {altri.map((p) => (
            <button type="button" key={p.id} className={`pp${scelta === p.id ? ' on' : ''}`} onClick={() => setPersona(p.id)}><Iniziale persona={p} taglia={28} />{p.nome}</button>
          ))}
          {/* il chip "Tutte" c'è per chi ha davvero qualcun altro da guardare: non lo fa comparire l'indirizzo */}
          {puoVedereTutte(dati, isAdmin) && (
            <button type="button" className={`pp${scelta === 'tutte' ? ' on' : ''}`} onClick={() => setPersona('tutte')}><i className="a col-tutti" style={{ width: 28, height: 28, fontStyle: 'normal' }}>✓</i>Tutte</button>
          )}
        </div>
      </div>

      {/* Il backup del database non c'era da diciotto giorni e il lavoro di GitHub diceva «success» ogni notte:
          nessuna pagina lo guardava. Adesso, se l'ultimo ha più di 36 ore, lo si legge qui — in alto, in rosso,
          dove non si può non vederlo (backend/oggi.py, `stato_backup`). Solo per gli admin. */}
      {backup?.vecchio && (
        <p className="avviso-backup" role="alert" data-testid="avviso-backup"
          style={{ border: '1px solid rgba(217,122,108,.5)', background: 'rgba(217,122,108,.09)', color: 'var(--bad)',
            borderRadius: 12, padding: '.6rem .8rem', marginBottom: '1rem', fontSize: '.85rem' }}>
          <b>Backup:</b> {backup.testo}
        </p>
      )}

      {/* con il filtro persona acceso quello che si scrive va nell'agenda di chi si guarda, non nella mia: prima
          l'attività nasceva assegnata a me e la giornata mostrata non cambiava di una riga (il messaggio verde
          diceva lo stesso "In agenda oggi alle 23: …"). Guardando la propria giornata, o "Tutte", non cambia niente */}
      <Tuttofare onFatto={ricarica} persona={altrui ? scelta : null} nomePersona={altrui ? nomeScelta : ''} />

      {/* singolare e plurale: prima "1 proposte aperte", "1 documenti da verificare" */}
      <div className={`nums r${nonLetti ? '' : ' cinque'}`}>
        <button type="button" className="num hot" onClick={() => document.getElementById('oggi-mie')?.scrollIntoView({ behavior: 'smooth' })}><b>{dati.cose}</b><span>da fare oggi</span></button>
        <button type="button" className="num" onClick={() => navigate('/admin/agenda?vista=mese')}><b>{n.visite_settimana}</b><span>{n.visite_settimana === 1 ? 'visita questa settimana' : 'visite questa settimana'}</span></button>
        <button type="button" className="num" onClick={() => navigate('/admin/clienti?fase=proposta')}><b>{n.proposte_aperte}</b><span>{n.proposte_aperte === 1 ? 'proposta aperta' : 'proposte aperte'}</span></button>
        <button type="button" className={`num${n.clienti_fermi ? ' bad' : ''}`} onClick={() => navigate('/admin/clienti?fermi=1')}><b>{n.clienti_fermi}</b><span>{n.clienti_fermi === 1 ? 'cliente fermo' : 'clienti fermi'}</span></button>
        <button type="button" className="num" onClick={() => navigate('/admin/documenti')}><b>{n.documenti_da_verificare}</b><span>{n.documenti_da_verificare === 1 ? 'documento da verificare' : 'documenti da verificare'}</span></button>
        {/* Case che qualcuno ha messo in lista e che l'estrattore non ha ancora letto: si vede finché non sono zero,
            senza soglie di ore, perché è l'unica cosa che dice "c'è un cliente che non vede niente"
            (backend/priorita_coda.py, non_letti) */}
        {nonLetti && (
          <button type="button" className={`num${nonLetti.aspetta_troppo ? ' bad' : ''}`} data-testid="annunci-non-letti"
            onClick={() => navigate('/admin/impostazioni#estrattore-attivita')}>
            <b>{nonLetti.quanti}</b>
            <span>{nonLetti.quanti === 1 ? 'annuncio non ancora letto' : 'annunci non ancora letti'}
              <small>il più vecchio {nonLetti.eta_testo}</small>
            </span>
          </button>
        )}
      </div>

      <div className="g3o">
        <section className="card hero r" id="oggi-mie">
          {/* di chi è la giornata: la persona scelta è quella che rimanda il server (dati.persona), il nome quello
              che manda lui (dati.persone); la pagina non ci mette del suo */}
          <div className="ttl"><div className="i"><Icona nome="orologio" /></div><h2>Oggi <small>{scelta === 'tutte' ? 'tutte' : scelta === dati.io ? 'le mie' : ((dati.persone || []).find((p) => p.id === scelta)?.nome || '')}</small></h2><Link to="/admin/agenda">Agenda →</Link></div>
          <div className="rows">
            {dati.mie.length === 0 && !dati.suggeriti.length && <p className="vuoto">Niente in agenda per oggi.</p>}
            {raggruppaPerCliente(dati.mie).map((b) => (b.tipo === 'voce'
              ? <RigaVoce key={b.voce.id} v={b.voce} onCambia={ricarica} isAdmin={isAdmin} />
              : (
                <div className="gruppo-cliente" key={`cliente-${b.cliente.id}`} data-testid={`gruppo-${b.cliente.id}`}>
                  <div className="testa"><Link to={linkCliente(b.cliente.id, userType)}>{b.cliente.nome}</Link><small>{quantiTesto(b.voci.length, 'cosa', 'cose')}</small></div>
                  <div className="rows">{b.voci.map((v) => <RigaVoce key={v.id} v={v} onCambia={ricarica} isAdmin={isAdmin} senzaCliente />)}</div>
                </div>
              )))}
            {dati.suggeriti.map((s) => <Suggerito key={s.id} s={s} onFatto={ricarica} />)}
            {/* un elenco tagliato dice quanti ne restano e dove vederli */}
            {altri6 && (
              <div className="altri-sug" data-testid="altri-suggeriti">
                <Link to="/admin/agenda">{testoAltriSuggeriti(altri6.quanti)} →</Link>
              </div>
            )}
          </div>
          {/* «aste escluse»: un'asta giudiziaria non si propone e non si suggerisce mai (backend/aste.py) */}
          {dati.suggeriti.some((s) => s.azione?.tipo === 'proponi_a' || s.azione?.tipo === 'proponi') && (
            <p data-testid="aste-escluse" style={{ color: 'var(--mute)', fontSize: '.72rem', marginTop: '.4rem' }}>aste escluse</p>
          )}
          {est && (
            <Link to="/admin/impostazioni#estrattore" className="ext"><i className={est.attivo ? undefined : 'spento'} />
              Estrattore {est.attivo ? 'attivo' : 'fermo'} · ultimo battito {minutiFa(est.secondi)} · {est.in_coda} in coda · {quantiTesto(est.fatti_oggi, 'annuncio letto', 'annunci letti')} oggi{est.captcha ? ` · ${est.captcha} captcha da risolvere` : ''}
            </Link>
          )}
          {/* Case che un cliente ha messo in lista e che l'estrattore non ha ancora letto: è la cosa che va detta
              per prima, perché c'è qualcuno che aspetta e non vede niente (backend/priorita_coda.py) */}
          {/* Perché adesso non parte niente: la stessa frase del riquadro in Impostazioni, dalla stessa
              funzione (backend/stato_coda.py). Prima qui non c'era e là diceva «senza lavori da fare» con
              trenta lavori in coda. Si mostra solo quando c'è qualcosa da spiegare. */}
          {est?.coda_stato?.motivi?.length > 0 && (
            <div className="ext-perche" data-testid="estrattore-perche">{est.coda_stato.frase}</div>
          )}
          {est?.clienti_in_attesa_frase && (
            <p data-testid="clienti-in-attesa" style={{ color: 'var(--bad)', fontSize: '.78rem', marginTop: '.5rem' }}>
              {est.clienti_in_attesa_frase}
            </p>
          )}
          {/* Due o più captcha in giornata: i portali stanno guardando. Al terzo sullo stesso portale l'estrattore si ferma lì fino a domani */}
          {est && est.captcha_oggi >= 2 && (
            <p data-testid="captcha-oggi" style={{ color: 'var(--warn)', fontSize: '.78rem', marginTop: '.5rem' }}>
              {est.captcha_oggi} captcha oggi: i portali stanno guardando. Al terzo sullo stesso portale l'estrattore si ferma lì fino a domani: andateci piano con le pagine.
            </p>
          )}
          {/* Case con un link che l'estrattore non sa leggere (un link di condivisione, un accorciatore, il sito
              dell'agenzia): le salta per sempre, e prima nessuno lo diceva — una era in lista da maggio senza
              essere mai stata letta. Si rimedia solo a mano, incollando il link del portale (backend/oggi.py) */}
          {linkStorti.length > 0 && (
            <p data-testid="link-non-leggibili" style={{ color: 'var(--warn)', fontSize: '.78rem', marginTop: '.5rem' }}>
              {linkStorti.length === 1 ? 'Una casa ha il link non leggibile' : `${linkStorti.length} case hanno il link non leggibile`}
              , quindi non verr{linkStorti.length === 1 ? 'à' : 'anno'} mai lett{linkStorti.length === 1 ? 'a' : 'e'}: aprit{linkStorti.length === 1 ? 'ela' : 'ele'} e incollate il link del portale.{' '}
              {linkStorti.slice(0, 3).map((c, k) => (
                <React.Fragment key={c.id}>{k > 0 && ', '}<Link to={`/immobili/${c.id}`}>{c.titolo}</Link></React.Fragment>
              ))}
              {linkStorti.length > 3 && ` e altre ${linkStorti.length - 3}`}
            </p>
          )}
        </section>

        {/* Case che aspettano una persona: l'estrattore ha aperto la pagina e non ha capito (due letture di fila
            senza prezzo, o un prezzo troppo diverso da quello salvato). Ha fatto la cosa giusta a non scrivere
            niente, ma intanto quelle case sono fuori dalle proponibili **e** fuori dal giro dei ricontrolli: senza
            questa card resterebbero ferme per sempre, in silenzio (backend/ricontrolli.py, backend/oggi.py) */}
        {/* Una casa venduta non si può più comprare: è la telefonata da fare, e se era in corsa va sostituita.
            Non ci sono bottoni perché non c'è niente da decidere — lo dice il portale nei suoi dati. L'avviso
            resta due settimane e poi sparisce da sé; la casa resta nella sua scheda con l'etichetta. */}
        {vendute.length > 0 && (
          <section className="card r" data-testid="case-vendute">
            <div className="ttl"><div className="i b"><Icona nome="immobili" /></div>
              <h2>Vendute o affittate <small>{vendute.length}</small></h2></div>
            <div className="rows persone">
              {vendute.map((c) => (
                <div key={c.id}>
                  <div style={{ minWidth: 0 }}>
                    <b><Link to={`/immobili/${c.id}`}>{c.titolo}</Link></b>
                    <span>{c.testo}</span>
                  </div>
                  {c.cliente_id && <Link className="btn sm" to={`/admin/clienti/${c.cliente_id}#immobili`}>Apri cliente</Link>}
                </div>
              ))}
            </div>
          </section>
        )}
        {daVerificare.length > 0 && (
          <section className="card r" data-testid="da-verificare-a-mano">
            <div className="ttl"><div className="i b"><Icona nome="cerca" /></div>
              <h2>Da verificare a mano <small>{daVerificare.length}</small></h2></div>
            <div className="rows persone">
              {daVerificare.map((c) => (
                <div key={c.id}>
                  <div style={{ minWidth: 0 }}>
                    <b><Link to={`/immobili/${c.id}`}>{c.titolo}</Link></b>
                    <span>{c.cliente ? `${c.cliente} · ` : ''}{c.testo}</span>
                  </div>
                  <div className="azioni">
                    <button type="button" className="btn sm p" onClick={() => rispondiVerifica(c, 'ancora-disponibile')}>Ancora disponibile</button>
                    <button type="button" className="btn sm" onClick={() => rispondiVerifica(c, 'non-piu-online')}>Non più online</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="card r">
          <div className="ttl"><div className="i b"><Icona nome="whatsapp" /></div><h2>Dai clienti <small>oggi</small></h2></div>
          <div className="rows dai">
            {dati.dai_clienti.length === 0 && <p className="vuoto">Niente di nuovo dai clienti nelle ultime 24 ore.</p>}
            {dati.dai_clienti.map((e) => (
              <div key={`${e.tipo}-${e.cliente_id}-${e.immobile_id || ''}`} className={e.azione2 ? 'sotto' : undefined}>
                <div style={{ minWidth: 0 }}><span className={`tag ${TAG[e.tipo] || 't-o'}`}>{e.etichetta}</span><b>{e.titolo}</b>{e.dettaglio && <span>{e.dettaglio}</span>}</div>
                <div className="azioni">
                  {e.azione?.tipo === 'fissa_visita'
                    ? <button type="button" className="btn sm p" onClick={() => setFissa({ clienteId: e.cliente_id, clienteNome: e.cliente_nome, immobileId: e.azione.immobile_id })}>{e.azione.etichetta}</button>
                    : e.azione?.tipo === 'chiama_agenzia'
                      ? (linkChiama(e.azione.telefono) ? <a className="btn sm p" href={linkChiama(e.azione.telefono)}>{e.azione.etichetta}</a>
                        : <Link className="btn sm p" to={`/immobili/${e.immobile_id}`}>Apri</Link>)
                      : <Link className={`btn sm${e.tipo === 'documenti' ? ' p' : ''}`} to={linkAzione(e, userType)}>{e.azione?.etichetta || 'Apri'}</Link>}
                  {e.azione2?.tipo === 'fissa_visita' && (
                    <button type="button" className="btn sm" onClick={() => setFissa({ clienteId: e.cliente_id, clienteNome: e.cliente_nome, immobileId: e.azione2.immobile_id })}>{e.azione2.etichetta}</button>
                  )}
                </div>
                {e.tipo === 'annunci_nuovi' && e.annunci?.length > 0 && (
                  <div className="annunci-brevi">
                    {/* «È un'asta» (l'asta sfuggita al filtro esce da qui e insegna) e, sulle «sospette asta»,
                        «Non è un'asta» per sbloccarle (backend/aste_apprendimento.py, ricerca_automatica.py) */}
                    {e.annunci.map((a) => <AnnuncioBreve key={a.id} a={a} onAsta={ricarica} />)}
                    {e.altri > 0 && <Link className="muto" style={{ fontSize: '.76rem' }} to={linkAzione(e, userType)}>e {e.altri === 1 ? 'un altro' : `altri ${e.altri}`} nella scheda →</Link>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <div className="stack">
          {/* Le persone che non sono ancora clienti si perdono se nessuno le richiama: non hanno una pratica
              aperta che le tenga a galla (backend/contatti.py, docs/contatti.md) */}
          {dati.contatti?.quanti > 0 && (
            <section className="card r" data-testid="contatti-da-sentire">
              <div className="ttl"><div className="i b"><Icona nome="persone" /></div>
                <h2>Contatti da sentire <small>{dati.contatti.quanti}</small></h2>
                <Link to="/admin/contatti?filtro=da_sentire">Tutti →</Link></div>
              {/* «persone»: la riga non ha il numero davanti come le altre card, quindi il testo va nella colonna
                  che si stringe — se no sul telefono i bottoni finiscono fuori dalla card */}
              <div className="rows persone">
                {dati.contatti.elenco.map((c) => (
                  <div key={c.id} data-testid={`contatto-${c.id}`}>
                    <div style={{ minWidth: 0 }}>
                      <b>{c.nome}</b>
                      <span>{[c.perche, c.prossimo_passo || c.cerca, c.fonte].filter(Boolean).join(' · ')}</span>
                    </div>
                    <div className="azioni">
                      {c.telefono && <a className="btn sm" href={linkChiama(c.telefono)} aria-label={`Chiama ${c.nome}`}><Icona nome="telefono" /></a>}
                      <Link className="btn sm p" to={`/admin/contatti/${c.id}`}>Apri</Link>
                    </div>
                  </div>
                ))}
              </div>
              {dati.contatti.senza_prossimo_passo > 0 && (
                <p className="muto" style={{ marginTop: '.5rem' }}>
                  <Link to="/admin/contatti?filtro=senza_prossimo_passo">
                    {dati.contatti.senza_prossimo_passo} senza un prossimo passo
                  </Link>
                </p>
              )}
            </section>
          )}
          <section className="card rosso r">
            <div className="ttl"><div className="i r"><Icona nome="orologio" /></div><h2>Clienti fermi</h2>{dati.fermi.length > 3 && <Link to="/admin/clienti?fermi=1">Tutti ({dati.fermi.length}) →</Link>}</div>
            <div className="rows">
              {dati.fermi.length === 0 && <p className="vuoto">Nessun cliente fermo da due settimane.</p>}
              {dati.fermi.slice(0, 3).map((f) => (
                <div key={f.cliente_id}>
                  <div className="n">{f.giorni}<small>giorni</small></div>
                  <div style={{ minWidth: 0 }}><b>{f.nome}</b><span>{f.dettaglio}</span></div>
                  <Link className="btn sm" to={linkCliente(f.cliente_id, userType)}>Apri</Link>
                </div>
              ))}
            </div>
          </section>
          {dati.altri_oggi.map(({ persona: p, voci }) => (
            <section className="card r" key={p.id}>
              <div className="ttl"><div className="i v"><Icona nome="profilo" /></div><h2>{p.nome} oggi</h2>
                {/* senza il numero: la giornata di là conta anche la mossa suggerita, e "Tutte (9)" che porta a
                    "Karolina, 10 cose prima di sera" sembrava un conto sbagliato */}
                {altreVoci(voci) > 0 && <Link to={linkGiornata(p.id)}>Tutta la giornata →</Link>}</div>
              <div className="rows">
                {voci.length === 0 && <p className="vuoto">Niente in agenda.</p>}
                {voci.slice(0, TAGLIO_ALTRI_OGGI).map((v) => (
                  <div key={v.id} className={v.stato === 'fatta' ? 'fatta' : undefined}>
                    <div className="h">{v.ora || '—'}<small>{v.stato === 'fatta' ? 'fatta' : PICCOLO[v.tipo]}</small></div>
                    <div style={{ minWidth: 0 }}>{v.cosa ? <CosaVoce cosa={v.cosa} tipo={v.tipo} voce={v} /> : <b>{v.titolo}</b>}</div>
                    {v.stato === 'fatta' ? <span style={{ color: 'var(--ok)' }}>✓</span> : linkVoce(v) ? <Link className="btn sm" to={linkVoce(v)}>Apri</Link> : <span />}
                  </div>
                ))}
                {/* le altre non spariscono: si dice quante sono e dove sono, come per i suggeriti e le scadenze */}
                {altreVoci(voci) > 0 && (
                  <div className="altri-sug" data-testid={`altre-voci-${p.id}`}>
                    <Link to={linkGiornata(p.id)}>{testoAltreVoci(altreVoci(voci), p.nome)} →</Link>
                  </div>
                )}
              </div>
            </section>
          ))}
          <section className="card r">
            <div className="ttl"><div className="i w"><Icona nome="orologio" /></div><h2>Prossime scadenze</h2></div>
            <div className="rows">
              {dati.scadenze.length === 0 && <p className="vuoto">Nessuna scadenza nel prossimo mese.</p>}
              {dati.scadenze.map((v) => (
                <div key={v.id}>
                  {/* con il mese quando la scadenza non è di questo mese (e l'anno quando non è di quest'anno):
                      "6" con sotto "MAR" per il 6 ottobre si leggeva come un 6 già passato, o come marzo */}
                  <div className="h">{daIso(v.data).getDate()}<small>{[meseSeDiverso(v.data, giornata), GIORNI_BREVI[daIso(v.data).getDay()]].filter(Boolean).join(' · ')}</small></div>
                  <div style={{ minWidth: 0 }}><b>{v.titolo}</b><span>{[v.cliente_breve, TIPI[v.tipo]?.nome, v.ora].filter(Boolean).join(' · ')}</span></div>
                  {/* la riga diceva cosa scade e quando, e non c'era niente da premere: era l'ultimo elenco della
                      pagina senza la sua strada. "Apri" porta alla cosa (immobile, cliente, agenzia) come nelle
                      altre righe; quando la voce non ha niente da aprire, al suo giorno in agenda */}
                  <Link className="btn sm" to={linkVoce(v) || `/admin/agenda?giorno=${v.data}`}>Apri</Link>
                </div>
              ))}
              {/* un elenco tagliato dice quante ne restano e dove vederle, come per i suggeriti */}
              {altreScad && (
                <div className="altri-sug" data-testid="altre-scadenze">
                  <Link to="/admin/agenda?vista=mese">{testoAltreScadenze(altreScad.quante)} →</Link>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      <ListaClienti limite={8} aggiorna={giro} />
      {fissa && <FissaVisita clienteId={fissa.clienteId} clienteNome={fissa.clienteNome || ''} immobileId={fissa.immobileId || ''}
        onChiudi={() => setFissa(null)} onFatto={() => { setFissa(null); ricarica(); }} />}
    </main>
  );
};

export default Oggi;
